'use client';

import styles from './EXPERIMENT_TravelMoneyDeliveryOptions.module.scss';
import {
  BLOCKS,
  Document,
  TopLevelBlock,
  Text,
} from '@contentful/rich-text-types';
import RenderRichContent from '@JohnLewisPartnership/jlfs-ui-library/dist/components/RenderRichContent/RenderRichContent';
import { Badge } from '@JohnLewisPartnership/jl-design-system/dist/components/badge/Badge';
import Cta from '@JohnLewisPartnership/jlfs-ui-library/dist/components/cta/cta';
import Countdown from '@JohnLewisPartnership/jlfs-ui-library/dist/components/countdown/countdown';
import {
  getNextDeliveryOrCollectionDay,
  getNextOrderDate,
} from '../../utils/travelMoneyDeliveryCountdown/travelMoneyDeliveryCountdown';
import { DateTime } from 'luxon';
import { useState, useMemo, useCallback, useEffect } from 'react';

export type TDeliveryIcon = 'RoyalMail' | 'JohnLewis' | 'Waitrose';

type TExperimentTravelMoneyDeliveryOptionsContainerProps = {
  Icon1: TDeliveryIcon;
  Icon2?: TDeliveryIcon;
  HeadingNote: string;
  Heading: string;
  HeadingSubtext: string;
  Delivery: Document;
  CollectionOrDeliveryTime: string;
  MinMaxOrder: Document;
  Currencies: Document;
  CurrenciesCommission: string;
  LinkLabel: string;
  LinkUrl: string;
  CurrentDateTime?: Date; // Only used for testing purposes locally, if not provided it will default to the current date and time
  MonetateInteractionId?: string;
};

export default function ExperimentTravelMoneyDeliveryOptionsContainer({
  Icon1,
  Icon2,
  HeadingNote,
  Heading,
  HeadingSubtext,
  Delivery,
  CollectionOrDeliveryTime,
  MinMaxOrder,
  Currencies,
  CurrenciesCommission,
  LinkLabel,
  LinkUrl,
  CurrentDateTime,
  MonetateInteractionId,
}: TExperimentTravelMoneyDeliveryOptionsContainerProps) {
  const renderIcon = (icon: TDeliveryIcon) => {
    switch (icon) {
      case 'RoyalMail':
        return <img src="/images/royal-mail.svg" alt="royal mail" />;
      case 'JohnLewis':
        return <img src="/images/john-lewis.svg" alt="john lewis" />;
      case 'Waitrose':
        return <img src="/images/waitrose.svg" alt="waitrose" />;
    }
  };

  const [timeNow, setTimeNow] = useState<DateTime>(() =>
    CurrentDateTime
      ? DateTime.fromJSDate(CurrentDateTime, { zone: 'Europe/London' })
      : DateTime.now().setZone('Europe/London')
  );
  const [isMockMode] = useState(() => CurrentDateTime != null);
  const [countdownEndDate, setCountdownEndDate] = useState<string | null>(null);

  const nextCollectionOrDeliveryDay = useMemo(
    () => getNextDeliveryOrCollectionDay(timeNow),
    [timeNow]
  );

  const nextOrder = useMemo(() => getNextOrderDate(timeNow), [timeNow]);
  const nextOrderDay = nextOrder?.orderDayName;
  const nextOrderDate = nextOrder?.orderDate;

  useEffect(() => {
    if (!nextOrderDate) {
      setCountdownEndDate(null);
      return;
    }

    if (!isMockMode) {
      setCountdownEndDate(new Date(nextOrderDate.toMillis()).toISOString());
      return;
    }

    const remainingMs = nextOrderDate.toMillis() - timeNow.toMillis();
    setCountdownEndDate(new Date(Date.now() + remainingMs).toISOString());
  }, [nextOrderDate, timeNow, isMockMode]);

  const handleCountdownComplete = useCallback(() => {
    // Advance just past the cutoff that fired so every derived value (next cutoff, delivery day, countdown end) rolls to the next slot.
    setTimeNow((prevTime) => {
      const firedCutoff = getNextOrderDate(prevTime)?.orderDate;
      return firedCutoff
        ? firedCutoff.plus({ seconds: 1 })
        : prevTime.plus({ days: 1 });
    });
  }, []);

  const deliveryWithCollectionText = useMemo(() => {
    if (!CollectionOrDeliveryTime || !nextCollectionOrDeliveryDay)
      return Delivery;

    const content = Array.isArray(Delivery.content)
      ? [...Delivery.content]
      : [];
    const lastIdx = content.length - 1;
    if (
      lastIdx >= 0 &&
      (content[lastIdx] as TopLevelBlock).nodeType === BLOCKS.PARAGRAPH
    ) {
      const boldInlineNode: Text = {
        nodeType: 'text',
        value: ` ${CollectionOrDeliveryTime} ${nextCollectionOrDeliveryDay}\n`,
        marks: [{ type: 'bold' }],
        data: {},
      };
      content[lastIdx] = {
        ...content[lastIdx],
        content: [
          ...((content[lastIdx] as TopLevelBlock).content || []),
          boldInlineNode,
        ],
      };
    }
    return { ...Delivery, content };
  }, [Delivery, CollectionOrDeliveryTime, nextCollectionOrDeliveryDay]);

  const isWithinCountdownWindow = useMemo(() => {
    if (!nextOrderDate) return false;
    return nextOrderDate.diff(timeNow, 'hours').hours <= 24;
  }, [nextOrderDate, timeNow]);

  return (
    <div className={styles.card}>
      <div className={styles.frame} aria-hidden="true" />
      <div className={styles.iconRow}>
        <span className={styles.iconContainer}>
          {renderIcon(Icon1)}
          {Icon2 && renderIcon(Icon2)}
        </span>
        <span className={styles.headingNote}>{HeadingNote}</span>
      </div>
      <div className={styles.heading}>{Heading}</div>
      <div className={styles.headingSubtext}>{HeadingSubtext}</div>
      <div className={styles.delivery}>
        <RenderRichContent
          content={deliveryWithCollectionText}
          section="DeliveryOptions"
          componentName="DeliveryOptionsText"
        />
        {CollectionOrDeliveryTime &&
          (countdownEndDate && isWithinCountdownWindow ? (
            <Countdown
              style="neon green style"
              title={'Order within '}
              endDate={countdownEndDate}
              inline
              footerLabel=""
              onComplete={handleCountdownComplete}
            />
          ) : nextOrderDay ? (
            `Order by 3pm on ${nextOrderDay}`
          ) : null)}
      </div>
      <div className={styles.minMaxOrder}>
        <RenderRichContent
          content={MinMaxOrder}
          section="MinMaxOrderOptions"
          componentName="MinMaxOrderText"
        />
      </div>
      <div className={styles.currenciesContainer}>
        <span className={styles.currencies}>
          <RenderRichContent
            content={Currencies}
            section="CurrenciesOptions"
            componentName="CurrenciesText"
          />
        </span>
        <span className={styles.currenciesCommision}>
          <Badge label={CurrenciesCommission} className={styles.badge} />
        </span>
      </div>
      <div className={styles.ctaContainer}>
        <Cta
          label={LinkLabel}
          type="underlined"
          target={LinkUrl}
          data-event="navigateToFresOrBureauPage"
          data-component={`TravelMoneyDeliveryCTA-${Heading.replace(/\s/g, '')}`}
          data-section="Ways to get your travel money"
          data-location={LinkUrl}
          monetateClickEvent={MonetateInteractionId}
        />
      </div>
    </div>
  );
}
